// -- Score color utilities (teal-based) --

export const scoreColor = (s) =>
  !s ? 'text-stone-400' : s >= 7 ? 'text-teal-600 dark:text-teal-400' : s >= 5 ? 'text-amber-600 dark:text-amber-400' : 'text-red-500';

export const scoreCardBg = (s) =>
  s >= 7 ? 'text-teal-700 dark:text-teal-300 bg-teal-50 dark:bg-teal-900/30 border-teal-200 dark:border-teal-800'
  : s >= 5 ? 'text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/30 border-amber-200 dark:border-amber-800'
  : 'text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/30 border-red-200 dark:border-red-800';

export const barColor = (s) =>
  s >= 7 ? 'bg-teal-500' : s >= 5 ? 'bg-amber-500' : 'bg-red-500';

// -- Date formatting (English only) --

export const formatDate = (date, _language, options = { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }) =>
  new Date(date).toLocaleString('en-US', options);

export const formatDateShort = (date, _language) =>
  new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

export const formatMonth = (date, _language) =>
  new Date(date).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

// -- Dimension name mapping (Research domain) --

export const DIMENSION_META = [
  { key: 'research_depth', label: 'Research Depth (50%)', description: 'Thoroughness and rigor of scientific thinking' },
  { key: 'productivity', label: 'Productivity (20%)', description: 'Concrete work accomplished' },
  { key: 'follow_through', label: 'Follow-Through (15%)', description: 'Following up on previous commitments' },
  { key: 'specificity', label: 'Specificity (15%)', description: 'Concrete details vs vague statements' },
];

// -- Status badge config --

export const STATUS_CONFIG = {
  uploading:    { label: 'Uploading',     bg: 'bg-blue-50 dark:bg-blue-900/30',   text: 'text-blue-600 dark:text-blue-400' },
  processing:   { label: 'Processing',    bg: 'bg-amber-50 dark:bg-amber-900/30',  text: 'text-amber-600 dark:text-amber-400' },
  failed:       { label: 'Failed',        bg: 'bg-red-50 dark:bg-red-900/30',      text: 'text-red-500 dark:text-red-400' },
  completed:    { label: 'Completed',     bg: 'bg-teal-50 dark:bg-teal-900/30',    text: 'text-teal-600 dark:text-teal-400' },
  fraud:        { label: 'Flagged',       bg: 'bg-red-600',                         text: 'text-white', bold: true },
  under_review: { label: 'Under Review',  bg: 'bg-amber-50 dark:bg-amber-900/30',  text: 'text-amber-600 dark:text-amber-400' },
};

// -- Byte size formatting --
// Shows small files as "X KB" instead of "0.0 MB" so a 12 KB recording
// doesn't look like an empty file.
export function formatBytes(bytes) {
  if (bytes == null || !Number.isFinite(bytes) || bytes < 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${Math.round(kb)} KB`;
  const mb = kb / 1024;
  if (mb < 10) return `${mb.toFixed(1)} MB`;
  return `${Math.round(mb)} MB`;
}

// -- Record session "done" screen copy --
// Pure mapping used by the post-upload landing screen. The old hardcoded
// "Upload successful!" was shown to the user BEFORE the upload actually
// completed (the submit handler kicks off runUpload without awaiting it
// and auto-navigates after 2.5s). This helper lets the screen reflect the
// real upload status so we don't lie to the user on slow connections.
export function uploadDoneCopy(status) {
  if (status === 'complete') {
    return {
      state: 'complete',
      headline: 'Upload successful!',
      subtext: 'Your attendance has been marked for today.',
    };
  }
  if (status === 'failed') {
    return {
      state: 'failed',
      headline: 'Upload failed',
      subtext: "We'll retry in the background. You can leave this page.",
    };
  }
  return {
    state: 'uploading',
    headline: 'Uploading…',
    subtext: 'You can leave this page — it will finish in the background.',
  };
}

export function StatusBadge({ status, className = '' }) {
  const cfg = STATUS_CONFIG[status];
  if (!cfg) return null;
  return (
    <span className={`text-[11px] font-semibold ${cfg.bg} ${cfg.text} px-3 py-1 rounded-full ${cfg.bold ? 'font-bold' : ''} ${className}`}>
      {cfg.label}
    </span>
  );
}
